/** Eingabehilfe und sanfte Formatprüfung für Kennzeichen (v. a. DE). */

import type { BookingMessages } from "@/i18n/types";
import { de } from "@/i18n/de";

const PLATE_CHARS = /[^A-ZÄÖÜ0-9\s-]/gi;
const PLATE_BODY = /^([A-ZÄÖÜ]{1,2})(\d{1,4}[EH]?)$/;

function cleanPlateInput(value: string): string {
  return value
    .trim()
    .toUpperCase()
    .replace(/[^A-ZÄÖÜ0-9\s-]/g, "");
}

function validPlateParts(city: string, letters: string, numbers: string): boolean {
  return (
    city.length >= 1 &&
    city.length <= 3 &&
    letters.length >= 1 &&
    letters.length <= 2 &&
    /^\d{1,4}[EH]?$/.test(numbers)
  );
}

function splitCompactPlate(compact: string): [string, string, string] | null {
  const matches: [string, string, string][] = [];
  for (let cityLen = 1; cityLen <= 3; cityLen += 1) {
    for (let letterLen = 1; letterLen <= 2; letterLen += 1) {
      if (cityLen + letterLen >= compact.length) continue;
      const city = compact.slice(0, cityLen);
      const letters = compact.slice(cityLen, cityLen + letterLen);
      const numbers = compact.slice(cityLen + letterLen);
      if (validPlateParts(city, letters, numbers)) {
        matches.push([city, letters, numbers]);
      }
    }
  }
  if (matches.length === 0) return null;
  if (matches.length === 1) return matches[0]!;

  const preferred = matches.filter(
    ([city, letters]) =>
      (city.length === 1 && letters.length === 2) ||
      (city.length === 2 && letters.length === 1),
  );
  if (preferred.length === 1) return preferred[0]!;

  matches.sort((a, b) => a[0].length - b[0].length || b[1].length - a[1].length);
  return matches[0]!;
}

function parseGermanPlate(value: string): [string, string, string] | null {
  const cleaned = cleanPlateInput(value);
  if (!cleaned) return null;

  if (cleaned.includes("-")) {
    const [cityPart, rest] = cleaned.split("-", 2);
    const city = cityPart!.replace(/\s+/g, "");
    const restCompact = rest!.replace(/\s+/g, "");
    const match = restCompact.match(PLATE_BODY);
    if (!match || !validPlateParts(city, match[1]!, match[2]!)) return null;
    return [city, match[1]!, match[2]!];
  }

  const compact = cleaned.replace(/[^A-ZÄÖÜ0-9]/g, "");
  return splitCompactPlate(compact);
}

/** Erlaubte Zeichen, Großbuchstaben, überflüssige Leerzeichen reduzieren. */
export function sanitizeLicensePlateInput(value: string): string {
  return value
    .toUpperCase()
    .replace(PLATE_CHARS, "")
    .replace(/\s+/g, " ")
    .trimStart()
    .slice(0, 14);
}

/** Lesbares Anzeigeformat (z. B. AB-C 1234). */
export function formatLicensePlateForDisplay(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return "";
  const parsed = parseGermanPlate(trimmed);
  if (!parsed) return sanitizeLicensePlateInput(trimmed);
  const [city, letters, numbers] = parsed;
  return `${city}-${letters} ${numbers}`;
}

/** Versucht ein deutsches Kennzeichen lesbar zu formatieren (z. B. AB-C 1234). */
export function formatLicensePlateOnBlur(value: string): string {
  return formatLicensePlateForDisplay(value);
}

/** Speicherformat: Bindestrich behalten, Leerzeichen entfernen (z. B. AB-C1234). */
export function normalizeLicensePlateForStorage(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return "";
  const parsed = parseGermanPlate(trimmed);
  if (!parsed) return trimmed.replace(/\s+/g, "").toUpperCase();
  const [city, letters, numbers] = parsed;
  return `${city}-${letters}${numbers}`;
}

/** Nicht blockierender Hinweis, wenn das Kennzeichen ungewöhnlich wirkt. */
export function getLicensePlateFormatHint(
  value: string,
  messages: Pick<BookingMessages["validation"], "licensePlate"> = de.validation,
): string | null {
  const trimmed = value.trim();
  if (!trimmed || trimmed.length < 4) return null;
  if (parseGermanPlate(trimmed)) return null;
  return messages.licensePlate;
}
